import { fg } from '@atlaskit/platform-feature-flags';

// ============================================================================
// AVP CHART CREATE
// ============================================================================

export const AVP_CHART_CREATE_KEY = 'avp_chart_create';

export type AvpChartCreateActionPayload = {
	/**
	 * The ARI of the AVP dashboard to create the chart on.
	 */
	dashboardAri: string;
	/**
	 * The chart data containing chart configuration and canvas layout.
	 */
	chartData: {
		/**
		 * The chart configuration.
		 */
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		chart: any;
		/**
		 * The canvas layout configuration.
		 */
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		canvasLayout: any;
	};
};

export function isAvpChartCreateActionData(data: unknown): data is AvpChartCreateActionPayload {
	if (typeof data !== 'object' || data === null) {
		return false;
	}
	const d = data as Record<string, unknown>;
	const chartData = d.chartData;
	if (typeof chartData !== 'object' || chartData === null) {
		return false;
	}
	const cd = chartData as Record<string, unknown>;
	return (
		typeof d.dashboardAri === 'string' &&
		typeof cd.chart === 'object' &&
		cd.chart !== null &&
		typeof cd.canvasLayout === 'object' &&
		cd.canvasLayout !== null
	);
}

// ============================================================================
// AVP CHART DELETE
// ============================================================================

export const AVP_CHART_DELETE_KEY = 'avp_chart_delete';

export type AvpChartDeleteActionPayload = {
	/**
	 * The ARI of the AVP dashboard to delete the chart from.
	 */
	dashboardAri: string;
	/**
	 * The identifier of the chart to delete.
	 */
	chartId: string;
	/**
	 * The canvas layout configuration.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	canvasLayout: any;
};

export function isAvpChartDeleteActionData(data: unknown): data is AvpChartDeleteActionPayload {
	if (typeof data !== 'object' || data === null || !fg('platform_avp_aifc_enabled')) {
		return false;
	}
	const d = data as Record<string, unknown>;
	return (
		typeof d.dashboardAri === 'string' &&
		typeof d.chartId === 'string' &&
		typeof d.canvasLayout === 'object' &&
		d.canvasLayout !== null
	);
}

// ============================================================================
// AVP CHART UPDATE
// ============================================================================

export const AVP_CHART_UPDATE_KEY = 'avp_chart_update';

export type AvpChartUpdateActionPayload = {
	/**
	 * The ARI of the AVP dashboard containing the chart.
	 */
	dashboardAri: string;
	/**
	 * The identifier of the chart to update.
	 */
	chartId: string;
	/**
	 * The chart data containing chart configuration.
	 */
	chartData: {
		/**
		 * The chart configuration.
		 */
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		chart: any;
	};
};

export function isAvpChartUpdateActionData(data: unknown): data is AvpChartUpdateActionPayload {
	if (typeof data !== 'object' || data === null || !fg('platform_avp_aifc_enabled')) {
		return false;
	}
	const d = data as Record<string, unknown>;
	if (typeof d.dashboardAri !== 'string' || typeof d.chartId !== 'string') {
		return false;
	}
	const chartData = d.chartData;
	if (typeof chartData !== 'object' || chartData === null) {
		return false;
	}
	const cd = chartData as Record<string, unknown>;
	return typeof cd.chart === 'object' && cd.chart !== null;
}

// ============================================================================
// AVP FILTER EXPRESSION CREATE
// ============================================================================

export type AvpFilterExpressionPayload = {
	/**
	 * The ARI of the AVP dashboard containing the filter expression.
	 */
	dashboardAri: string;
	/**
	 * The full updated filter expression tree to render on the dashboard.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	filterExpression: any;
};

const isStringArray = (value: unknown): value is string[] =>
	Array.isArray(value) && value.every((item) => typeof item === 'string');

const isAvpFilterExpressionPayload = (data: unknown): data is AvpFilterExpressionPayload => {
	if (typeof data !== 'object' || data === null || !fg('platform_avp_aifc_enabled')) {
		return false;
	}

	const d = data as Record<string, unknown>;
	return (
		typeof d.dashboardAri === 'string' &&
		(d.filterExpression === null ||
			(typeof d.filterExpression === 'object' && d.filterExpression !== null))
	);
};

export const AVP_FILTER_EXPRESSION_CREATE_KEY = 'avp_dashboard_create_filter';

export type AvpFilterExpressionCreateActionPayload = AvpFilterExpressionPayload & {
	/**
	 * The identifiers of the created filter expressions.
	 */
	expressionIds?: string[];
};

export function isAvpFilterExpressionCreateActionData(
	data: unknown,
): data is AvpFilterExpressionCreateActionPayload {
	if (!isAvpFilterExpressionPayload(data)) {
		return false;
	}

	const d = data as Record<string, unknown>;
	return d.expressionIds === undefined || isStringArray(d.expressionIds);
}

// ============================================================================
// AVP FILTER EXPRESSION DELETE
// ============================================================================

export const AVP_FILTER_EXPRESSION_DELETE_KEY = 'avp_dashboard_delete_filter';

export type AvpFilterExpressionDeleteActionPayload = AvpFilterExpressionPayload & {
	/**
	 * The identifiers of the filter expressions that were deleted.
	 */
	expressionIds: string[];
};

export function isAvpFilterExpressionDeleteActionData(
	data: unknown,
): data is AvpFilterExpressionDeleteActionPayload {
	if (!isAvpFilterExpressionPayload(data)) {
		return false;
	}

	const d = data as Record<string, unknown>;
	return isStringArray(d.expressionIds);
}

// ============================================================================
// AVP FILTER EXPRESSION UPDATE
// ============================================================================

export const AVP_FILTER_EXPRESSION_UPDATE_KEY = 'avp_dashboard_update_filter';

export type AvpFilterExpressionUpdateActionPayload = AvpFilterExpressionPayload & {
	/**
	 * The identifier of the filter expression that was updated.
	 */
	expressionId: string;
};

export function isAvpFilterExpressionUpdateActionData(
	data: unknown,
): data is AvpFilterExpressionUpdateActionPayload {
	if (!isAvpFilterExpressionPayload(data)) {
		return false;
	}

	const d = data as Record<string, unknown>;
	return typeof d.expressionId === 'string';
}

// ============================================================================
// AVP DASHBOARD CREATE
// ============================================================================

export const AVP_DASHBOARD_CREATE_KEY = 'avp_dashboard_create';

export type AvpDashboardCreateActionPayload = {
	/**
	 * The URL of the AVP dashboard.
	 */
	dashboardUrl: string;
	/**
	 * The ARI of the AVP dashboard.
	 */
	dashboardAri: string;
};

export function isAvpDashboardCreateActionData(
	data: unknown,
): data is AvpDashboardCreateActionPayload {
	if (typeof data !== 'object' || data === null) {
		return false;
	}
	const d = data as Record<string, unknown>;
	return typeof d.dashboardUrl === 'string' && typeof d.dashboardAri === 'string';
}

// ============================================================================
// AVP ACTION CLASSIFICATION
// ============================================================================

const AVP_CREATABLE_ACTION_KEYS = new Set([
	AVP_CHART_CREATE_KEY,
	AVP_DASHBOARD_CREATE_KEY,
	AVP_FILTER_EXPRESSION_CREATE_KEY,
]);

const AVP_PREVIEW_CARD_ACTION_KEYS = new Set([AVP_DASHBOARD_CREATE_KEY]);

export const isAvpPreviewCardAction = (action: { key: string }): boolean =>
	AVP_PREVIEW_CARD_ACTION_KEYS.has(action.key);

const AVP_DELETABLE_ACTION_KEYS = new Set([AVP_CHART_DELETE_KEY, AVP_FILTER_EXPRESSION_DELETE_KEY]);

const AVP_UPDATABLE_ACTION_KEYS = new Set([AVP_CHART_UPDATE_KEY, AVP_FILTER_EXPRESSION_UPDATE_KEY]);

export const isAvpCreatableAction = (action: { key: string }): boolean =>
	AVP_CREATABLE_ACTION_KEYS.has(action.key);

export const isAvpDeletableAction = (action: { key: string }): boolean =>
	AVP_DELETABLE_ACTION_KEYS.has(action.key);

export const isAvpUpdatableAction = (action: { key: string }): boolean =>
	AVP_UPDATABLE_ACTION_KEYS.has(action.key);

export const isAvpStreamAction = (action: { key: string }): boolean =>
	isAvpCreatableAction(action) || isAvpDeletableAction(action) || isAvpUpdatableAction(action);
